import type { FunctionComponent } from "react";
import { memo, useEffect } from "react";
import { graphql, useFragment } from "react-relay";

import { useStoryStyleContext } from "~/components/story-style/contexts";
import { FloatClickableArea } from "~/components/story-style/story-style-float-clickable-area";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { storyStyleCardArticle$key } from "~/queries/__generated__/storyStyleCardArticle.graphql";

import { StoryStyleCardContext } from "./contexts";
import { StoryStyleCardImage } from "./image";
import { StoryStyleCardLink } from "./link";
import { StoryStyleCardSection } from "./section";
import { CardInfoContainer, Container, StyledCoverLogo } from "./styles";
import { StoryStyleCardTitle } from "./title";

export type Props = {
  hasBottomBannerAd: boolean;
  hasCoverLogo: boolean;
  index: number;
  isHidden: boolean;
  reference: storyStyleCardArticle$key;
  section?: string;
};

export const StoryStyleCard: FunctionComponent<Props> = memo(
  ({ hasBottomBannerAd, hasCoverLogo, index, isHidden, reference: reference_, section }) => {
    const { handleGoToNextPage, handleGoToPreviousPage } = useStoryStyleContext();
    const storyStyleCard = useFragment(
      graphql`
        fragment storyStyleCardArticle on Article {
          ...imageStoryStyleCardImageArticle
          ...titleStoryStyleCardTitleArticle
          ...linkStoryStyleCardLinkArticle
        }
      `,
      reference_,
    );

    useEffect(() => {
      if (isHidden) return;
      sendGATracking({
        action: `Story Card/Style/Impression`,
        category: "Story Card",
        value: index + 1,
      });

      sendGA4Tracking({
        action: "imp",
        category: "storycard",
        customized_parameters: {
          card_num: index + 1,
          section_name: "Style",
        },
        subcategory: "",
      });
    }, [index, isHidden]);

    return (
      <Container $isHidden={isHidden}>
        <StoryStyleCardContext.Provider value={{ articleCardIndex: index }}>
          <StoryStyleCardImage reference={storyStyleCard} />
          {hasCoverLogo && <StyledCoverLogo />}
          <FloatClickableArea
            handleGoToNextPage={handleGoToNextPage}
            handleGoToPreviousPage={handleGoToPreviousPage}
          />
          <CardInfoContainer $hasBottomBannerAd={hasBottomBannerAd}>
            {section && <StoryStyleCardSection section={section} />}
            <StoryStyleCardTitle reference={storyStyleCard} />
          </CardInfoContainer>
          <StoryStyleCardLink $hasBottomBannerAd={hasBottomBannerAd} reference={storyStyleCard} />
        </StoryStyleCardContext.Provider>
      </Container>
    );
  },
);

StoryStyleCard.displayName = "StoryStyleCard";
